// src/game/map/corruptionSystem.js

export const CORRUPTION_PHASES = {
  INFESTATION: 'infestation',
  DOMINANCE: 'dominance',
  COLLAPSE: 'collapse',
};

const CORRUPTIBLE_TILES = new Set(['floor', 'corridor']);

export const DEFAULT_CORRUPTION_CONFIG = {
  initialCoverage: 0.25,

  phase2Threshold: 0.70,
  collapseThreshold: 1.0,

  spreadEveryActions: 5,
  spreadEveryActionsAfterEnemiesCleared: 10,
  spreadEveryActionsDominance: 3,

  pauseIfAllBraziersLit: true,
  cleanseLightTurnsRequired: 2,

  dominanceTorchDuration: 48,

  specialEnemySpawnChanceMin: 0.15,
  specialEnemySpawnChanceMax: 0.30,

  clusterSeedCountMin: 2,
  clusterSeedCountMax: 5,

  preferTwoNeighborWeight: 2.5,
  jumpSpreadChance: 0.18,

  avoidSpawnRadius: 4,
};

function mulberry32(seed) {
  let t = seed >>> 0;
  return function () {
    t += 0x6D2B79F5;
    let r = Math.imul(t ^ (t >>> 15), 1 | t);
    r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

export function tileKey(x, y) {
  return `${x},${y}`;
}

export function parseTileKey(key) {
  const [x, y] = String(key).split(',').map(Number);
  return { x, y };
}

export function isCorruptibleTile(tile) {
  return CORRUPTIBLE_TILES.has(tile);
}

export function isCorruptionEnabledForMood(mood) {
  return !!mood && mood.id === 'corrupted';
}

export function getCorruptionConfig(mood) {
  return {
    ...DEFAULT_CORRUPTION_CONFIG,
    ...(mood?.corruption || {}),
  };
}

function inBounds(map, x, y) {
  return (
    Array.isArray(map) &&
    y >= 0 &&
    y < map.length &&
    Array.isArray(map[y]) &&
    x >= 0 &&
    x < map[y].length
  );
}

function getCardinalNeighbors(map, x, y) {
  const out = [];
  const dirs = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
  ];

  for (const [dx, dy] of dirs) {
    const nx = x + dx;
    const ny = y + dy;
    if (inBounds(map, nx, ny)) out.push({ x: nx, y: ny });
  }

  return out;
}

function getJumpCandidates(map, x, y) {
  const out = [];
  const dirs = [
    [2, 0],
    [-2, 0],
    [0, 2],
    [0, -2],
  ];

  for (const [dx, dy] of dirs) {
    const nx = x + dx;
    const ny = y + dy;
    if (inBounds(map, nx, ny)) out.push({ x: nx, y: ny });
  }

  return out;
}

function manhattan(a, b) {
  return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
}

function findEligibleTiles(map, spawn, config) {
  const out = [];

  if (!Array.isArray(map) || !map.length) return out;

  for (let y = 0; y < map.length; y++) {
    for (let x = 0; x < map[y].length; x++) {
      const tile = map[y][x];
      if (!isCorruptibleTile(tile)) continue;

      if (spawn && manhattan({ x, y }, spawn) < config.avoidSpawnRadius) continue;

      out.push({ x, y, key: tileKey(x, y) });
    }
  }

  return out;
}

function countCorruptedNeighbors(map, corruptedKeys, x, y) {
  let total = 0;

  for (const n of getCardinalNeighbors(map, x, y)) {
    if (corruptedKeys.has(tileKey(n.x, n.y))) total += 1;
  }

  return total;
}

function pickRandom(arr, rng) {
  if (!arr.length) return null;
  return arr[Math.floor(rng() * arr.length)] || null;
}

function growCluster({
  map,
  rng,
  corruptedKeys,
  frontier,
  targetCount,
}) {
  while (corruptedKeys.size < targetCount && frontier.length) {
    const idx = Math.floor(rng() * frontier.length);
    const node = frontier.splice(idx, 1)[0];
    if (!node) continue;

    const k = tileKey(node.x, node.y);
    if (corruptedKeys.has(k)) continue;
    if (!isCorruptibleTile(map[node.y]?.[node.x])) continue;

    corruptedKeys.add(k);

    for (const n of getCardinalNeighbors(map, node.x, node.y)) {
      if (!isCorruptibleTile(map[n.y]?.[n.x])) continue;
      if (corruptedKeys.has(tileKey(n.x, n.y))) continue;
      frontier.push(n);
    }
  }
}

export function getCorruptionCoverage(corruptionState, map) {
  const eligible = findEligibleTiles(map, null, {
    ...DEFAULT_CORRUPTION_CONFIG,
    avoidSpawnRadius: 0,
  });

  const total = eligible.length;
  const corrupted = corruptionState?.corruptedKeys instanceof Set
    ? [...corruptionState.corruptedKeys].filter((k) => {
        const { x, y } = parseTileKey(k);
        return inBounds(map, x, y) && isCorruptibleTile(map[y][x]);
      }).length
    : 0;

  return {
    corrupted,
    total,
    ratio: total > 0 ? corrupted / total : 0,
  };
}

export function getCorruptionPhase(coverageRatio, config) {
  if (coverageRatio >= config.collapseThreshold) return CORRUPTION_PHASES.COLLAPSE;
  if (coverageRatio >= config.phase2Threshold) return CORRUPTION_PHASES.DOMINANCE;
  return CORRUPTION_PHASES.INFESTATION;
}

export function buildInitialCorruptionState({
  map,
  floor,
  runSeed,
  spawn,
  config,
}) {
  const safeConfig = { ...DEFAULT_CORRUPTION_CONFIG, ...(config || {}) };
  const rng = mulberry32(
    (((Number(runSeed) || 0) ^ (Number(floor) || 1) * 0x9e3779b9) ^ 0x51a7f00d) >>> 0
  );

  const eligible = findEligibleTiles(map, spawn, safeConfig);
  const targetCount = Math.max(1, Math.floor(eligible.length * safeConfig.initialCoverage));

  const corruptedKeys = new Set();
  const exposureByKey = {};

  const seedCount =
    safeConfig.clusterSeedCountMin +
    Math.floor(
      rng() * (safeConfig.clusterSeedCountMax - safeConfig.clusterSeedCountMin + 1)
    );

  const seedPool = [...eligible];
  const frontier = [];

  for (let i = 0; i < seedCount && seedPool.length; i++) {
    const idx = Math.floor(rng() * seedPool.length);
    const picked = seedPool.splice(idx, 1)[0];
    if (!picked) continue;
    frontier.push({ x: picked.x, y: picked.y });
  }

  growCluster({
    map,
    rng,
    corruptedKeys,
    frontier,
    targetCount,
  });

  const coverage = getCorruptionCoverage({ corruptedKeys }, map);
  const phase = getCorruptionPhase(coverage.ratio, safeConfig);

  let heartKey = null;
  let bestScore = -Infinity;

  for (const k of corruptedKeys) {
    const { x, y } = parseTileKey(k);
    const neighbors = countCorruptedNeighbors(map, corruptedKeys, x, y);
    if (neighbors > bestScore) {
      bestScore = neighbors;
      heartKey = k;
    }
  }

  return {
    enabled: true,
    phase,
    coverageRatio: coverage.ratio,
    lastProcessedActionCount: 0,
    lastSpreadAtActionCount: 0,
    corruptedKeys,
    exposureByKey,
    heartKey,
    specialEnemyKeys: new Set(),
    dominanceAnnounced: phase !== CORRUPTION_PHASES.INFESTATION,
    collapseStarted: false,
    bossSpawned: false,
    bossDefeated: false,
    stepsHidden: false,
    stepsDownOriginalPos: null,
  };
}

export function isTileCorrupted(corruptionState, x, y) {
  return !!corruptionState?.corruptedKeys?.has?.(tileKey(x, y));
}

function getSpreadInterval({
  phase,
  allEnemiesCleared,
  allBraziersLit,
  config,
}) {
  if (allBraziersLit && config.pauseIfAllBraziersLit) return Infinity;

  if (phase === CORRUPTION_PHASES.DOMINANCE || phase === CORRUPTION_PHASES.COLLAPSE) {
    return config.spreadEveryActionsDominance;
  }

  if (allEnemiesCleared) {
    return config.spreadEveryActionsAfterEnemiesCleared;
  }

  return config.spreadEveryActions;
}

function buildSpreadCandidates(map, corruptedKeys, config, rng) {
  const candidates = [];

  for (const k of corruptedKeys) {
    const { x, y } = parseTileKey(k);

    for (const n of getCardinalNeighbors(map, x, y)) {
      const nk = tileKey(n.x, n.y);
      if (corruptedKeys.has(nk)) continue;
      if (!isCorruptibleTile(map[n.y]?.[n.x])) continue;

      const corruptedNeighbors = countCorruptedNeighbors(map, corruptedKeys, n.x, n.y);
      const weight =
        corruptedNeighbors >= 2
          ? config.preferTwoNeighborWeight
          : 1;

      candidates.push({
        x: n.x,
        y: n.y,
        key: nk,
        weight,
      });
    }

    if (rng() < config.jumpSpreadChance) {
      for (const j of getJumpCandidates(map, x, y)) {
        const jk = tileKey(j.x, j.y);
        if (corruptedKeys.has(jk)) continue;
        if (!isCorruptibleTile(map[j.y]?.[j.x])) continue;

        candidates.push({
          x: j.x,
          y: j.y,
          key: jk,
          weight: 0.75,
        });
      }
    }
  }

  return candidates;
}

function weightedPick(candidates, rng) {
  if (!candidates.length) return null;
  const total = candidates.reduce((sum, c) => sum + Math.max(0.01, Number(c.weight) || 0), 0);
  let roll = rng() * total;

  for (const c of candidates) {
    roll -= Math.max(0.01, Number(c.weight) || 0);
    if (roll <= 0) return c;
  }

  return candidates[candidates.length - 1] || null;
}

function updateExposureByLight({
  corruptionState,
  isTileLit,
}) {
  const nextExposureByKey = {};

  for (const k of corruptionState.corruptedKeys) {
    const { x, y } = parseTileKey(k);
    const lit = !!isTileLit(x, y);
    const prev = Number(corruptionState.exposureByKey?.[k] || 0);
    nextExposureByKey[k] = lit ? (prev + 1) : 0;
  }

  return nextExposureByKey;
}

function cleanseExposedTiles({
  corruptedKeys,
  exposureByKey,
  cleanseLightTurnsRequired,
}) {
  const nextCorruptedKeys = new Set();
  const nextExposureByKey = {};
  const cleansedKeys = [];

  for (const k of corruptedKeys) {
    const exposure = Number(exposureByKey?.[k] || 0);
    if (exposure >= cleanseLightTurnsRequired) {
      cleansedKeys.push(k);
      continue;
    }

    nextCorruptedKeys.add(k);
    nextExposureByKey[k] = exposure;
  }

  return {
    nextCorruptedKeys,
    nextExposureByKey,
    cleansedKeys,
  };
}

export function advanceCorruptionState({
  corruptionState,
  map,
  actionCount,
  config,
  isTileLit,
  allEnemiesCleared,
  allBraziersLit,
}) {
  if (!corruptionState?.enabled) {
    return {
      nextState: corruptionState,
      events: {},
    };
  }

  if (actionCount <= Number(corruptionState.lastProcessedActionCount || 0)) {
    return {
      nextState: corruptionState,
      events: {},
    };
  }

  const safeConfig = { ...DEFAULT_CORRUPTION_CONFIG, ...(config || {}) };

  const rng = mulberry32(
    (((actionCount * 2654435761) ^ 0x77e8a221) ^ (corruptionState.corruptedKeys.size * 97)) >>> 0
  );

  const exposureByKey = updateExposureByLight({
    corruptionState,
    isTileLit,
  });

  const cleanseResult = cleanseExposedTiles({
    corruptedKeys: corruptionState.corruptedKeys,
    exposureByKey,
    cleanseLightTurnsRequired: safeConfig.cleanseLightTurnsRequired,
  });

  let nextCorruptedKeys = cleanseResult.nextCorruptedKeys;
  let nextExposureByKey = cleanseResult.nextExposureByKey;

  const coverageBeforeSpread = getCorruptionCoverage(
    { corruptedKeys: nextCorruptedKeys },
    map
  );

  let nextPhase = getCorruptionPhase(coverageBeforeSpread.ratio, safeConfig);

  const spreadInterval = getSpreadInterval({
    phase: nextPhase,
    allEnemiesCleared,
    allBraziersLit,
    config: safeConfig,
  });

  let didSpread = false;
  let spawnedSpecialEnemyKey = null;
  let lastSpreadAtActionCount = Number(corruptionState.lastSpreadAtActionCount || 0);

  if (
    Number.isFinite(spreadInterval) &&
    actionCount - lastSpreadAtActionCount >= spreadInterval
  ) {
    const candidates = buildSpreadCandidates(map, nextCorruptedKeys, safeConfig, rng);
    const picked = weightedPick(candidates, rng);

    if (picked) {
      nextCorruptedKeys = new Set(nextCorruptedKeys);
      nextCorruptedKeys.add(picked.key);
      nextExposureByKey[picked.key] = 0;
      lastSpreadAtActionCount = actionCount;
      didSpread = true;

      const phaseForSpawn = getCorruptionPhase(
        getCorruptionCoverage({ corruptedKeys: nextCorruptedKeys }, map).ratio,
        safeConfig
      );

      if (phaseForSpawn === CORRUPTION_PHASES.DOMINANCE) {
        const chance =
          safeConfig.specialEnemySpawnChanceMin +
          rng() * (safeConfig.specialEnemySpawnChanceMax - safeConfig.specialEnemySpawnChanceMin);

        if (rng() < chance) {
          spawnedSpecialEnemyKey = picked.key;
        }
      }
    }
  }

  const finalCoverage = getCorruptionCoverage(
    { corruptedKeys: nextCorruptedKeys },
    map
  );
  nextPhase = getCorruptionPhase(finalCoverage.ratio, safeConfig);

  let heartKey = corruptionState.heartKey;
  let bestScore = -Infinity;

  for (const k of nextCorruptedKeys) {
    const { x, y } = parseTileKey(k);
    const neighbors = countCorruptedNeighbors(map, nextCorruptedKeys, x, y);
    if (neighbors > bestScore) {
      bestScore = neighbors;
      heartKey = k;
    }
  }

  const dominanceTriggered =
    nextPhase === CORRUPTION_PHASES.DOMINANCE &&
    !corruptionState.dominanceAnnounced;

  const collapseTriggered =
    nextPhase === CORRUPTION_PHASES.COLLAPSE &&
    !corruptionState.collapseStarted;

  const nextSpecialEnemyKeys = new Set(corruptionState.specialEnemyKeys || []);
  if (spawnedSpecialEnemyKey) {
    nextSpecialEnemyKeys.add(spawnedSpecialEnemyKey);
  }

  const nextState = {
    ...corruptionState,
    phase: nextPhase,
    coverageRatio: finalCoverage.ratio,
    lastProcessedActionCount: actionCount,
    lastSpreadAtActionCount,
    corruptedKeys: nextCorruptedKeys,
    exposureByKey: nextExposureByKey,
    heartKey,
    specialEnemyKeys: nextSpecialEnemyKeys,
    dominanceAnnounced:
      corruptionState.dominanceAnnounced || dominanceTriggered,
    collapseStarted:
      corruptionState.collapseStarted || collapseTriggered,
  };

  return {
    nextState,
    events: {
      didSpread,
      cleansedKeys: cleanseResult.cleansedKeys,
      dominanceTriggered,
      collapseTriggered,
      spawnedSpecialEnemyKey,
      finalCoverageRatio: finalCoverage.ratio,
      nextPhase,
    },
  };
}

export function purgeCorruptionState(corruptionState) {
  if (!corruptionState) return null;

  return {
    ...corruptionState,
    phase: CORRUPTION_PHASES.INFESTATION,
    coverageRatio: 0,
    corruptedKeys: new Set(),
    exposureByKey: {},
    specialEnemyKeys: new Set(),
    bossDefeated: true,
    collapseStarted: false,
    bossSpawned: false,
    stepsHidden: false,
  };
}